/**
 * תוכן צ׳קליסט — גרסה 1.0 (D37, D38; מסלול התוכן, D11). מאושר מקצועית בידי המשתמש (37-1).
 *
 * מקור: `CHECKLIST-CONTENT-v1.0.he.md` בתיקיית המסמכים, §2, **על גבי 0.2** (`checklistContent02.ts`)
 * — 20 פרקים, 143 סעיפים: שני סעיפים חדשים (C4-06, C9-08 — אחרונים בפרקים שלהם), 25 סעיפים שנוסחם
 * דויק לפי ק״ת 12061 ולפי הדוגמאות (§2.3), והתחולה של D38 (§2.4). כל השאר — בדיוק כמו ב־0.2. זה
 * **חומר ייבוא**: מנהל מייבא אותו כטיוטה ״1.0״ ומפרסם; המנוע והמסכים קוראים את התוכן מהמסד, לא
 * מכאן (CQ-04). 0.1 ו־0.2 אינן משתנות (RQ-D11).
 *
 * **אף קוד קיים אינו משנה משמעות ואינו נמחק,** וסעיף חדש מקבל את הקוד הבא בפרק שלו — כך ההתאמה
 * לפי קוד בין גרסאות (D32, ״לסמן גם בביקור הזה״) נשמרת.
 *
 * `isDemo: false` — הפרסום מהמסך הוא האישור המקצועי, בשם המפרסם ובתאריך הפרסום (D37 ס׳ 3). סטטוס
 * האימות נשמר בכל סעיף כנתון לבקרה פנימית: ״נוסח אומת״ ב־30 סעיפים, השאר ״לא מאומת״. האישור אינו
 * אימות של כל תקנה שהסעיפים נשענים עליה (GAP-04, GAP-05). מספרי תקנות בעמודת המקור — רק בסעיף
 * ״נוסח אומת״ (שם אומתו מול ק״ת 12061, R-19…R-30); בסעיף ״לא מאומת״ — שם התקנה בלבד, כמו ב־0.2.
 * ״כמו C4-01״ שבמסמך (המקור של C4-02) הורחב לנוסח המלא, כמו ״כנ״ל״ ב־0.2.
 *
 * תחולה (D38, תשובה 38-1):
 * - תמיד — פרקים 1, 2, 3, 5, 6, 7, 8, 9, 20. כללי העובדות של 0.2 נשארים: C2-05 (שטח היתר) ·
 *   C5-03, C6-09 (מנהל אתר) · C5-06, C5-07 (ביקור חוזר) · C6-02 (אתר המחייב הודעה) · C6-05
 *   (עובדים זרים).
 * - לפי שלב העבודה (`stage_kind_any`, כלל מוצר) — 11 חפירות · 12 ביסוס · 13 שלד · 19 גמר, כמו
 *   ב־0.2, ועכשיו גם 10 עבודה בגובה ו־14 פיגומים — בשלב שלד או גמר. לא נבחר שלב — הפרק מופיע
 *   (״נדרש בירור״, RQ-D06).
 * - לבחירה (`optional`) — 4, 15, 16, 17, 18: אינם בביקור עד שהבקר בוחר בהם, בפתיחה או במהלך
 *   הביקור. C4-01 ו־C4-02 מאבדים את כלל ״אתר המחייב הודעה״ — כל הפרק לבחירה.
 * - C19-06 (״עבודות שיפוץ / מבנה פעיל״) נשאר לפי שלב גמר בלבד, כמו ב־0.2 — ממתין להכרעה (A-115).
 */

import {
  type ApplicabilityRule,
  type ChecklistContent,
  type ContentChapter,
  type ContentQuestion,
} from './checklistContent.ts';
import type { StageKind } from './siteProfile.ts';

const always: ApplicabilityRule = { kind: 'always' };

const BIG_SITE: ApplicabilityRule = {
  kind: 'fact',
  fact: 'permitAreaAtLeastSiteManagerThreshold',
  equals: 'yes',
  label: 'שטח ההיתר 15,000 מ״ר ומעלה',
};
const NOTICE_SITE: ApplicabilityRule = {
  kind: 'fact',
  fact: 'requiresSection192Notice',
  equals: 'yes',
  label: 'האתר מחייב הודעה לפי סעיף 192',
};
const SITE_MANAGER: ApplicabilityRule = {
  kind: 'fact',
  fact: 'hasActiveSiteManager',
  equals: 'yes',
  label: 'קיים מנהל אתר',
};
const FOLLOW_UP_VISIT: ApplicabilityRule = {
  kind: 'fact',
  fact: 'isFollowUpVisit',
  equals: 'yes',
  label: 'ביקור שאינו ראשון באתר',
};
const FOREIGN_WORKERS: ApplicabilityRule = {
  kind: 'fact',
  fact: 'employsForeignWorkers',
  equals: 'yes',
  label: 'מועסקים באתר עובדים זרים',
};
const OPTIONAL: ApplicabilityRule = { kind: 'optional', label: 'פרק לבחירה' };
const stage = (kinds: StageKind[], label: string): ApplicabilityRule => ({
  kind: 'stage_kind_any',
  kinds,
  label,
});
/**
 * פרקים 10 עבודה בגובה ו־14 פיגומים (D38, תשובה 38-1) — בשלב שלד או גמר. התווית נכנסת לסיבת
 * הסינון (״לא נבחר שלב שלד או גמר וחזיתות״) ולצ׳יפ ״לפי שלב: …״.
 */
const SKELETON_OR_FINISHING = stage(['skeleton', 'finishing'], 'שלד או גמר וחזיתות');

/** סעיף ״לא מאומת״ (ברירת המחדל). */
const u = (
  code: string,
  title: string,
  whatToCheck: string,
  sourceText: string,
  applicability: ApplicabilityRule = always,
): ContentQuestion => ({
  code,
  title,
  whatToCheck,
  sourceText,
  verification: 'unverified',
  applicability,
});
/** סעיף ״נוסח אומת״ — רק היכן שהמסמך מסמן כך (30 סעיפים, §2.3). */
const v = (
  code: string,
  title: string,
  whatToCheck: string,
  sourceText: string,
  applicability: ApplicabilityRule = always,
): ContentQuestion => ({
  ...u(code, title, whatToCheck, sourceText, applicability),
  verification: 'text_verified',
});

const BUILDING = 'תקנות הבטיחות בעבודה (עבודות בנייה)';
const HEIGHT = 'תקנות הבטיחות בעבודה (עבודה בגובה)';
const PPE = 'תקנות הבטיחות בעבודה (ציוד מגן אישי)';
const TRAINING = 'תקנות ארגון הפיקוח על העבודה (מסירת מידע והדרכת עובדים)';
const SAFETY_OFFICERS = 'תקנות ארגון הפיקוח על העבודה (ממונים על הבטיחות)';
const SAFETY_PLAN = 'תקנות ארגון הפיקוח על העבודה (תוכנית לניהול הבטיחות)';
const ORDINANCE = 'פקודת הבטיחות בעבודה';
const LIFTING = 'תקנות מכונות הרמה';
const CRANE_OPERATORS = 'תקנות הבטיחות בעבודה (עגורנאים, מפעילי מכונות הרמה אחרות ואתתים)';
const ELECTRICITY = 'תקנות החשמל';
const FIRE = 'דיני כבאות';

/** פרק עם סימוני ״תקין / לא תקין / לא רלוונטי״ (כל הפרקים פרט לפרק 1). */
const chapter = (code: string, title: string, questions: ContentQuestion[]): ContentChapter => ({
  code,
  title,
  scheme: 'standard',
  questions,
});

/**
 * פרק ״לפי שלב העבודה״ (CQ-07, D38) — אותו כלל שלב לכל סעיף בפרק. ב־1.0 כלל יכול לכלול יותר
 * מסוג שלב אחד (SKELETON_OR_FINISHING).
 */
const stageChapter = (
  code: string,
  title: string,
  rule: ApplicabilityRule,
  questions: ContentQuestion[],
): ContentChapter => ({
  code,
  title,
  scheme: 'standard',
  questions: questions.map((q) => ({ ...q, applicability: rule })),
});

/** פרק ״לבחירה״ (D38): כל סעיף בו `optional` — אינו בביקור עד שהבקר בוחר בפרק. */
const optionalChapter = (
  code: string,
  title: string,
  questions: ContentQuestion[],
): ContentChapter => ({
  code,
  title,
  scheme: 'standard',
  questions: questions.map((q) => ({ ...q, applicability: OPTIONAL })),
});

export const CHECKLIST_1_0: ChecklistContent = {
  title: 'צ׳קליסט ביקור בטיחות',
  versionLabel: '1.0',
  isDemo: false,
  chapters: [
    {
      code: '1',
      title: 'תוכנית הבטיחות',
      scheme: 'plan_single',
      questions: [
        v(
          'C1-01',
          'קיום ויישום תוכנית הבטיחות במועד הביקור',
          'התוכנית נמצאת באתר ומצורפת לפנקס הכללי · סוג התוכנית מתאים למספר העובדים (עד 49 — תוכנית ארגון בטיחות; 50 ומעלה — תוכנית לניהול הבטיחות) · הגרסה עדכנית לשלב הבנייה ולסיכונים שזוהו · המענים שבתוכנית מיושמים בשטח · התוכנית חתומה בידי מבצע הבנייה · מבצע הבנייה אישר את התוכנית בחתימתו ומסר העתק לבקר ולמנהל האתר · באתר עד 49 עובדים — התוכנית כוללת את פרקי התוספת השלישית (תיאור, מדיניות וסיכון קביל, מערך בטיחות, ניהול סיכונים, הדרכות, בדיקות תקופתיות, היתרים והסמכות, נהלים)',
          'R-02, R-05, R-10, R-12, R-17; תקנה 2(א)(3); התוספת השלישית (R-30)',
        ),
      ],
    },
    chapter('2', 'מינויים ובעלי תפקידים', [
      v(
        'C2-01',
        'מונה בקר בטיחות מטעם המזמין והמינוי מתועד',
        'כתב מינוי, מועד תחילה, פרטי הבקר',
        'R-03',
      ),
      v(
        'C2-02',
        'הבקר אינו ממלא באתר תפקיד נוסף',
        'הבקר אינו מנהל העבודה, מנהל האתר או ממונה הבטיחות של אותו אתר',
        'R-03, R-11',
      ),
      v(
        'C2-03',
        'מונה מנהל עבודה הרשום במרשם מנהלי העבודה בסיווג המתאים, והוא נוכח ומשגיח בזמן ביצוע העבודות',
        'המינוי דווח בטופס המקוון והעתקו בפנקס הכללי · פרטיו בשלט (C9-08) · נוכחות בפועל בזמן הביקור',
        'תקנות 2(א)(1), 2(ב)',
      ),
      v(
        'C2-04',
        'מנהל העבודה מועסק באתר רק בתפקידים התואמים את תפקידו, ופנוי לפקח',
        'אינו מבצע במקביל עבודה שמונעת ממנו לפקח',
        'תקנות 2(ב), 2ג',
      ),
      v(
        'C2-05',
        'באתר ששטח ההיתר שלו 15,000 מ״ר ומעלה מונה מנהל אתר',
        'כתב מינוי; כשירות (מהנדס או הנדסאי רשום)',
        'R-12',
        BIG_SITE,
      ),
      u(
        'C2-06',
        'מונה ממונה בטיחות כנדרש, ותחומי אחריותו ידועים לבעלי התפקידים',
        'כתב מינוי, אישור כשירות, היקף נוכחות',
        SAFETY_OFFICERS,
      ),
      v(
        'C2-07',
        'רישום מנהל העבודה במרשם מנהלי העבודה בתוקף, וההסמכות של בעלי התפקידים בתוקף',
        'הרישום בתוקף (חמש שנים, וחידוש לפי התקנות) · תעודות בתוקף לשאר בעלי התפקידים',
        'תקנות 2ד, 3, 3א (R-29)',
      ),
      v(
        'C2-08',
        'בעל תפקיד שסיים את תפקידו הוחלף במינוי חדש',
        'אין פער ללא בעל תפקיד; ההחלפה דווחה בטופס המקוון (ראו C4-03); אחרי שמנהל העבודה הפסיק — העבודה לא נמשכה עד מינוי חדש',
        'R-03; תקנות 2(ג), 2(ד)',
      ),
    ]),
    chapter('3', 'הסכם, מסמכי התחלה ותוכניות הנדסיות', [
      v(
        'C3-01',
        'בהסכם בין המזמין למבצע הבנייה מפורטים המשאבים ליישום תוכנית הבטיחות, לפי פרקי התוכנית והסיכונים שבה',
        'ההסכם קיים וחתום; כשהמזמין הוא מבצע הבנייה — המשאבים מפורטים בתוכנית עצמה',
        'תקנה 1א(א)(2)',
      ),
      v(
        'C3-02',
        'המזמין אישר בכתב להתחיל את העבודות רק אחרי שהוכנה תוכנית בטיחות, נחתם הסכם ומונה בקר',
        'מועד האישור מול מועדי המסמכים; התוכנית מצורפת לפנקס הכללי',
        'תקנות 1א(א), 2(א)(4)',
      ),
      v(
        'C3-03',
        'קיימות תוכניות הנדסיות לכל מה שהתקנות מחייבות, ומנהל האתר אישר זאת בחתימתו',
        'גידור משטחים מעל 2 מ׳ · פיגומים מיוחדים, ממוכנים, תלויים, שלוחים וזיזיים · טפסות כשנדרש · מבני מתכת מעל 6 מ׳ — תוכנית שהכין מהנדס רשום או רשוי, מצורפת לפנקס הכללי ונמצאת באתר',
        'הגדרת ״תוכנית הנדסית״; תקנות 2ב(ב)(3), 9, 17א, 45, 58, 69א, 85, 106 (R-21)',
      ),
    ]),
    optionalChapter('4', 'הודעות ודיווח לרשויות', [
      v(
        'C4-01',
        'הוגשה למפקח העבודה האזורי הודעה מקוונת על התחלת העבודות ועל מינוי הבקר, מנהל העבודה ומנהל האתר, והאישור על הגשתה מצורף לפנקס הכללי',
        'אישור הגשת ההודעה בפנקס',
        'תקנה 6א(א)(1), (ב); התוספת השנייה, חלק א׳ (R-25)',
      ),
      v(
        'C4-02',
        'פרטי בעלי התפקידים בהודעה תואמים את בעלי התפקידים בפועל',
        'השוואה בין ההודעה לבעלי התפקידים באתר',
        'תקנה 6א(א)(1), (ב); התוספת השנייה, חלק א׳ (R-25)',
      ),
      v(
        'C4-03',
        'החלפה או סיום תפקיד של בקר, מנהל עבודה או מנהל אתר דווחו בטופס המקוון',
        'הטופס והעתקו בפנקס הכללי',
        'תקנות 1א(ב), 2(ג), 6א(א)(3); התוספת השנייה, חלק ג׳',
      ),
      u(
        'C4-04',
        'הפסקת עבודה בשל סכנה חמורה דווחה למפקח העבודה',
        'תיעוד ההפסקה והדיווח',
        `${ORDINANCE}; ${BUILDING}`,
      ),
      u(
        'C4-05',
        'תאונות עבודה ו״כמעט תאונות״ מתועדות ומדווחות לפי הנוהל',
        'יומן אירועים; דיווח למפקח ולמוסד לביטוח לאומי כשנדרש',
        'פקודת תאונות ומחלות משלח יד (הודעה)',
      ),
      u(
        'C4-06',
        'אין עבודה בניגוד לצו שנתן מפקח עבודה, והצווים שניתנו לאתר מתועדים',
        'הצווים בפנקס הכללי; העבודה בשטח מול הצו',
        'חוק ארגון הפיקוח על העבודה; התוספת הראשונה לתקנות (סיבה 10)',
      ),
    ]),
    chapter('5', 'תיעוד, בקרה ומעקב ליקויים', [
      v(
        'C5-01',
        'פנקס כללי קיים באתר ומעודכן, ומצורפים אליו המינויים, האישורים, הדיווחים, הדוחות וההודעות',
        'כולל דוחות הבקר, דוחות מנהל האתר, דיווחי הממונה וההודעות למפקח העבודה',
        'תקנה 2(ט) (R-26)',
      ),
      v(
        'C5-02',
        'מנהל העבודה מבצע בדיקה יומית ומתעד אותה',
        'רישום יומי בפנקס הכללי של ביקורת גידור החלקים המסוכנים במכונות, ושל בדיקת האתר',
        'R-16',
      ),
      v(
        'C5-03',
        'מנהל האתר אישר בחתימתו את המענים לסיכונים לפי שלבי הבנייה, ועורך בקרה מתועדת לפחות אחת ל־30 ימים על יישומם',
        'דוחות הבקרה שנמסרו למבצע הבנייה',
        'R-14; תקנה 2ב(ב)(1)–(2)',
        SITE_MANAGER,
      ),
      v(
        'C5-04',
        'ממונה הבטיחות (אם מונה) מדווח למנהל העבודה ולמנהל האתר על מפגעים והפרות, לכל המאוחר בתוך 48 שעות',
        'בדיווח גם מפגעים מדיווחים קודמים שטרם הוסרו; העתק בפנקס הכללי',
        'תקנה 5א(א)(1)–(2) (R-27)',
      ),
      u(
        'C5-05',
        'קיים מעקב מתועד אחר תיקון ליקויים והוא מעודכן',
        'רשימת ליקויים עם סטטוס, אחראי ומועד',
        SAFETY_PLAN,
      ),
      u(
        'C5-06',
        'הליקויים מדוח הבקר הקודם טופלו ומצב כל אחד מהם ידוע',
        'השוואה מול הדוח הקודם (במוצר: דוח מעקב — שלב 6ב)',
        'R-05, R-09',
        FOLLOW_UP_VISIT,
      ),
      v(
        'C5-07',
        'המזמין אישר בכתב בתוך 48 שעות שניתן מענה לדיווח הבקר הקודם',
        'אישור בכתב ומועדו',
        'R-09 / SCH-03',
        FOLLOW_UP_VISIT,
      ),
      u(
        'C5-08',
        'תעודות בדיקה, תסקירים ואישורים נגישים באתר',
        'תיק מסמכים זמין לעיון',
        `${ORDINANCE}; ${BUILDING}`,
      ),
    ]),
    chapter('6', 'הדרכות והכשרות', [
      v(
        'C6-01',
        'הדרכות מתקיימות בהתאם לסיכוני האתר ומתועדות',
        'נושא, תאריך, מדריך, רשימת נוכחים',
        'R-05',
      ),
      v('C6-02', 'ההדרכה ניתנה פנים אל פנים', 'לא הדרכה מרחוק בלבד', 'T-03', NOTICE_SITE),
      u('C6-03', 'כל עובד חדש הודרך לפני תחילת עבודתו', 'תיעוד הדרכת כניסה לכל עובד חדש', TRAINING),
      u(
        'C6-04',
        'ההדרכה ניתנת בשפה שהעובדים מבינים',
        'שפת ההדרכה מול שפת העובדים; תרגום כשנדרש',
        TRAINING,
      ),
      v(
        'C6-05',
        'עובד זר קיבל הדרכה נוספת בתוך חודש מכניסתו לישראל לראשונה',
        'תיעוד ההדרכה ומועדה',
        'T-06 / SCH-07',
        FOREIGN_WORKERS,
      ),
      u(
        'C6-06',
        'עובדי קבלני המשנה הודרכו והדרכתם מתועדת',
        'רשימות נוכחים של קבלני משנה',
        TRAINING,
      ),
      u(
        'C6-07',
        'מפעילי ציוד (עגורן, מלגזה, מחפרון, במת הרמה) מחזיקים ברישיון או בהסמכה בתוקף',
        'תעודות מול המפעילים בפועל',
        `${CRANE_OPERATORS}; ${LIFTING}`,
      ),
      u('C6-08', 'העובדים בגובה מחזיקים בהסמכה בתוקף', 'תעודות הדרכה לעבודה בגובה', HEIGHT),
      v(
        'C6-09',
        'דיווחי ההדרכות מועברים למנהל האתר ונבדקים בבקרה החודשית',
        'דוח הדרכות בדוח הבקרה',
        'R-14',
        SITE_MANAGER,
      ),
    ]),
    chapter('7', 'ציוד מגן אישי', [
      u('C7-01', 'כל העובדים באתר חובשים קסדות מגן', 'תצפית בשטח', `${PPE}; R-05 (מיגון)`),
      u('C7-02', 'כל העובדים נועלים נעלי בטיחות', 'תצפית בשטח', `${PPE}; R-05 (מיגון)`),
      u(
        'C7-03',
        'אפודים זוהרים באזורי תנועת כלים ובעבודה ליד כביש',
        'תצפית בשטח',
        `${PPE}; R-05 (מיגון)`,
      ),
      u(
        'C7-04',
        'מיגון לפי המשימה: עיניים, שמיעה, נשימה, כפפות',
        'חיתוך, ריתוך, רעש, אבק — האם המיגון מתאים ובשימוש',
        `${PPE}; R-05 (מיגון)`,
      ),
      u(
        'C7-05',
        'ציוד המגן תקין, תקני, ומשתמשים בו בפועל',
        'מצב הציוד; שימוש נכון (קסדה רכוסה, רתמה מחוברת)',
        `${PPE}; R-05 (מיגון)`,
      ),
    ]),
    chapter('8', 'סדר, ניקיון ורווחה', [
      u(
        'C8-01',
        'מעברים, דרכי גישה וגרמי מדרגות פנויים ויציבים',
        'אין חסימות, חומרים או כבלים במעבר',
        BUILDING,
      ),
      u('C8-02', 'פסולת נאספת ומפונה; אין מסמרים בולטים בעץ משומש', 'סדר באזורי העבודה', BUILDING),
      u(
        'C8-03',
        'חומרים מאוחסנים ביציבות ובמרחק בטוח משפות, מחפירות ומפתחים',
        'ערימות יציבות, לא על קצה',
        BUILDING,
      ),
      u(
        'C8-04',
        'תאורה מספקת באזורי עבודה ובמעברים',
        'תאורה קבועה וזמנית, כולל בשעות חושך',
        BUILDING,
      ),
      u(
        'C8-05',
        'מתקני רווחה: שירותים, מי שתייה, מקום לאכילה',
        'שירותים נקיים · מי שתייה קרים · אזור מוצל למנוחה ולאכילה — קיימים, נגישים ותקינים',
        `${BUILDING}; תקנות הרווחה`,
      ),
    ]),
    chapter('9', 'גידור, בקרת כניסה ובטיחות הציבור', [
      u('C9-01', 'גדר היקפית רציפה ויציבה, ללא פרצות', 'הקפת האתר', BUILDING),
      u('C9-02', 'שילוט אזהרה בכניסות ובאזורי סכנה', 'שלטים קריאים, במקום הנכון', BUILDING),
      u('C9-03', 'בקרת כניסה: שער נסגר או שומר, ורישום נוכחים', 'מי נכנס, מי באתר', BUILDING),
      u(
        'C9-04',
        'יציאת משאיות וכלים מוסדרת: מכוון, שילוט, שדה ראייה',
        'צומת היציאה בפועל',
        BUILDING,
      ),
      u(
        'C9-05',
        'מעבר הולכי רגל מוגן בצמוד לאתר',
        'מדרכת מעבר מקורה / גדר / הפרדה מהתנועה',
        BUILDING,
      ),
      u(
        'C9-06',
        'הגנה מפני נפילת חפצים אל הרחוב ואל מבנים סמוכים',
        'רשתות, גגונים, מרפסות מוגנות',
        BUILDING,
      ),
      u('C9-07', 'אזור האחסון והפריקה אינו חוסם מדרכה או כביש', 'תצפית מחוץ לאתר', BUILDING),
      v(
        'C9-08',
        'בכניסה לאתר מוצב שלט עם פרטי בעלי התפקידים, ובכלל זה המזמין, בקר הבטיחות ומנהל האתר ומספרי הרישום שלהם',
        'השלט מול בעלי התפקידים בפועל · המזמין ומענו · הבקר — מספר רישום בפנקס המהנדסים או במרשם ההנדסאים, או אישור כשירות כממונה · מנהל האתר — מספר רישום · פרטי מבצע הבנייה ומנהל העבודה',
        'תקנה 7(4)–(6) לתקנות הבטיחות בעבודה (עבודות בנייה), בתיקון התשפ״ו',
      ),
    ]),
    stageChapter('10', 'עבודה בגובה ומניעת נפילה', SKELETON_OR_FINISHING, [
      u(
        'C10-01',
        'העובדים בגובה הוסמכו ומשתמשים ברתמות תקינות המחוברות בפועל',
        'רתמה, בולם, חיבור לנקודת עיגון',
        HEIGHT,
      ),
      u('C10-02', 'נקודות העיגון מתאימות ואושרו', 'סוג העיגון ומי אישר', HEIGHT),
      v(
        'C10-03',
        'בכל משטח עבודה ומדרכת מעבר שמהם אפשר ליפול מגובה העולה על 2 מ׳ — גידור עם אזן יד, אזן תיכון ולוח רגל, שהותקן לפי תוכנית הנדסית',
        'רציפות הגידור · התאמה לתוכנית · מנהל העבודה משגיח ומפסיק התקנה שאינה תואמת',
        'תקנה 9(א), (ג) (R-20)',
      ),
      u(
        'C10-04',
        'פתחי רצפה מכוסים בכיסוי יציב, מקובע ומסומן, או מגודרים',
        'הכיסוי אינו ניתן להזזה בטעות',
        `${BUILDING}; ${HEIGHT}`,
      ),
      u('C10-05', 'פירי מעליות ופתחים אנכיים מגודרים', 'בכל קומה', `${BUILDING}; ${HEIGHT}`),
      u('C10-06', 'גרמי מדרגות עם מעקה תקני', 'לא לוח עץ מאולתר', `${BUILDING}; ${HEIGHT}`),
      u(
        'C10-07',
        'סולמות תקינים, מקובעים, ובולטים מעל נקודת היציאה',
        'מצב הסולם ואופן ההצבה',
        `${BUILDING}; ${HEIGHT}`,
      ),
      u(
        'C10-08',
        'עבודה על גגות ועל משטחים שבירים מוגנת',
        'רשת, מעקה, רתמה, לוחות פיזור עומס',
        HEIGHT,
      ),
      u('C10-09', 'מניעת נפילת כלים וחומרים מגובה', 'לוח רגל, רשת, גידור האזור שמתחת', BUILDING),
      u(
        'C10-10',
        'עבודה בגובה מופסקת ברוח חזקה ובתנאי מזג אוויר מסוכנים',
        'הוראה ידועה; מי מחליט',
        HEIGHT,
      ),
    ]),
    stageChapter('11', 'חפירות, דיפון ועבודות עפר', stage(['excavation'], 'חפירות ודיפון'), [
      u('C11-01', 'החפירה תואמת את התוכנית ההנדסית', 'עומק, שיפועים, מידות', BUILDING),
      u(
        'C11-02',
        'קיים דיפון או שיפוע שמונע קריסה, והוא נבדק אחרי גשם או רעידה',
        'מצב הדפנות בפועל; רישום בדיקה',
        BUILDING,
      ),
      u('C11-03', 'ציוד כבד וערימות עפר מרוחקים משפת החפירה', 'מרחק בפועל', BUILDING),
      u(
        'C11-04',
        'קיימות דרכי גישה ומילוט מהחפירה',
        'סולם או מדרגות, במרחק סביר מכל נקודת עבודה',
        BUILDING,
      ),
      u('C11-05', 'שפת החפירה מגודרת ומסומנת', 'גידור ושילוט, גם בלילה', BUILDING),
      u(
        'C11-06',
        'תשתיות תת־קרקעיות אותרו וסומנו לפני החפירה',
        'חשמל, גז, מים, ביוב, תקשורת — תיאום עם הגופים',
        `${BUILDING}; חוק החשמל`,
      ),
      u('C11-07', 'מים מנוקזים ואין עבודה בחפירה מוצפת', 'ניקוז, שאיבה', BUILDING),
      u('C11-08', 'יציבות מבנים, כבישים ותשתיות סמוכים מנוטרת', 'תוכנית ניטור; תוצאות', BUILDING),
      u(
        'C11-09',
        'מכוון מלווה ציוד כבד שפועל ליד החפירה ולצד עובדים',
        'נוכחות מכוון; הפרדה',
        BUILDING,
      ),
    ]),
    stageChapter('12', 'ביסוס ויסודות', stage(['foundations'], 'ביסוס ויסודות'), [
      u('C12-01', 'פתחי כלונסאות וקידוחים מכוסים או מגודרים ומסומנים', 'כל פתח פתוח', BUILDING),
      u(
        'C12-02',
        'אזור עבודת הקידוח מגודר ונשמר מרחק בטוח מהמכונה',
        'הפרדת עובדים מהמכונה',
        BUILDING,
      ),
      u(
        'C12-03',
        'מוטות זיון בולטים מוגנים בכיפות או בכיסוי',
        'בכל מקום שעובדים עלולים ליפול עליהם',
        BUILDING,
      ),
      u(
        'C12-04',
        'שינוע כלובי זיון ורכיבים כבדים נעשה במתקן הרמה מתאים ובאביזרים תקינים',
        'אופן ההרמה והקשירה',
        `${BUILDING}; ${LIFTING}`,
      ),
      u(
        'C12-05',
        'מערך היציקה (משאבה, צנרת, גישה) מוצב ביציבות ומופעל בידי כשירים',
        'מצב הציוד והמפעיל',
        BUILDING,
      ),
      u(
        'C12-06',
        'תוכנית הביסוס ותנאי הקרקע אושרו בידי מהנדס לפני היציקה',
        'אישור מתועד',
        BUILDING,
      ),
      u(
        'C12-07',
        'חשמל וכבלים באזור רטוב מוגנים ומחוברים בבטחה',
        'לוחות, מפסקי פחת, כבלים מורמים',
        ELECTRICITY,
      ),
    ]),
    stageChapter('13', 'שלד, טפסנות ויציקות', stage(['skeleton'], 'שלד'), [
      v(
        'C13-01',
        'כשנדרשת תוכנית הנדסית לטפסות — הן הוקמו, שונו ופורקו לפיה ובהשגחת מנהל העבודה',
        'נדרשת כשהתקן מחייב תכן, כשרכיב קיים משמש דופן, או בטפסות מיוחדות (כגון מחליקות) · חומרים מתאימים וללא פגמים נראים',
        'תקנות 83, 85 (R-23)',
      ),
      v(
        'C13-02',
        'לפני יציקה בטפסות שנדרשת להן תוכנית הנדסית — מנהל העבודה בדק את המערכת, אישר אותה ומסר את אישורו למבצע הבנייה',
        'רישום תוצאות הבדיקה בפנקס הכללי',
        'תקנה 84 (R-23)',
      ),
      u('C13-03', 'תמיכות וסמכים תקינים, מיושרים ועומדים על בסיס יציב', 'מצב התמיכות', BUILDING),
      v(
        'C13-04',
        'מבנה מתכת שגובהו מעל 6 מ׳ מוקם לפי תוכנית הנדסית, בהשגחת מנהל העבודה',
        'תוכנית חתומה; התאמה בשטח',
        'תקנה 106 (R-24)',
      ),
      u('C13-05', 'מוטות זיון בולטים מכוסים', 'כיפות / כיסוי', BUILDING),
      u(
        'C13-06',
        'קצוות רצפות, פירי מעלית ופתחי מדרגות מוגנים בכל קומה',
        'מעקות וכיסויים לאורך השלד',
        BUILDING,
      ),
      u(
        'C13-07',
        'פירוק טפסות נעשה רק אחרי אישור חוזק הבטון',
        'אישור מהנדס / בדיקת חוזק',
        BUILDING,
      ),
      u(
        'C13-08',
        'אין העמסת חומרים על רצפות טריות או על טפסות מעבר לעומס המותר',
        'ערימות בלוקים/ברזל על תקרה טרייה',
        BUILDING,
      ),
      u(
        'C13-09',
        'אין עבודה מתחת למטען מונף, ואזור ההרמה מגודר',
        'תצפית בזמן הנפה',
        `${BUILDING}; ${LIFTING}`,
      ),
    ]),
    stageChapter('14', 'פיגומים ובמות עבודה', SKELETON_OR_FINISHING, [
      v(
        'C14-01',
        'פיגום זקפים שגובהו מעל 6 מ׳ הוקם, שונה ופורק בנוכחות ובהשגחה ישירה של בונה מקצועי לפיגומי זקפים, ולפי תוכנית כשנדרשת',
        'מי הבונה המקצועי · תוכנית הקמת פיגומים או תוכנית הנדסית',
        'תקנות 16(ב), 17(א)–(ב) (R-22)',
      ),
      u('C14-02', 'הפיגום נבדק לפני השימוש ובדיקתו מתועדת', 'תג / רישום בפנקס', BUILDING),
      u('C14-03', 'מעקה מלא בכל צד פתוח של משטח העבודה', 'אזן יד, אזן תיכון, לוח רגל', BUILDING),
      u(
        'C14-04',
        'רצפת העבודה שלמה, יציבה וללא מרווחים מסוכנים',
        'לוחות מחוברים, ללא רווחים',
        BUILDING,
      ),
      u('C14-05', 'הבסיס יציב והפיגום מעוגן למבנה', 'לוחות בסיס, עיגונים', BUILDING),
      u('C14-06', 'גישה בטוחה אל הפיגום ובתוכו', 'מדרגות או סולם מקובע, פתחי עלייה', BUILDING),
      v(
        'C14-07',
        'פיגום ממוכן, תלוי, שלוח או זיזי, ופיגום זקפים מיוחד (מעל 25 מ׳) — מוקמים, מותקנים, מועתקים, משונים ומפורקים רק לפי תוכנית הנדסית',
        'תוכנית חתומה בידי מהנדס · מנהל העבודה משגיח ומפסיק עבודה שאינה תואמת · תסקיר בתוקף ומפעיל מוסמך',
        'תקנות 17א, 45, 58, 69א; הגדרת ״פיגום זקפים מיוחד״ (R-22)',
      ),
      u(
        'C14-08',
        'פיגום נייד נבנה לפי הוראות היצרן, הגלגלים נעולים בזמן העבודה',
        'מצב הפיגום הנייד',
        `${BUILDING}; ${HEIGHT}`,
      ),
      u('C14-09', 'העומס המותר מוצג ואין העמסת יתר', 'שלט עומס; חומרים על הפיגום', BUILDING),
      u('C14-10', 'נשמר מרחק בטוח מקווי חשמל', 'פיגום ליד קו עילי', `${BUILDING}; ${ELECTRICITY}`),
    ]),
    optionalChapter('15', 'עגורנים וציוד הרמה', [
      u(
        'C15-01',
        'לכל עגורן ולכל אביזר הרמה תסקיר בדיקה בתוקף מבודק מוסמך',
        'תסקירים מול הציוד בשטח',
        `${ORDINANCE}; ${LIFTING}`,
      ),
      u(
        'C15-02',
        'מפעיל העגורן מורשה ומחזיק ברישיון תקף',
        'רישיון מול המפעיל בפועל',
        CRANE_OPERATORS,
      ),
      u(
        'C15-03',
        'מתן האותות בטוח: אתת מוסמך או קשר אלחוטי עם המפעיל',
        'מי מאותת ואיך; אין תקשורת לקויה בין מפעילי מכונות הרמה שונים לבין העובדים',
        `${CRANE_OPERATORS}; התוספת הראשונה (סיבה 3)`,
      ),
      u(
        'C15-04',
        'אביזרי ההרמה (כבלים, שרשראות, ווים, רצועות) תקינים, מסומנים וניתנים לזיהוי',
        'מצב האביזרים; סימון עומס',
        LIFTING,
      ),
      u('C15-05', 'מגבילי העומס פועלים ועומס העבודה הבטוח מוצג', 'בדיקה ושילוט', LIFTING),
      u(
        'C15-06',
        'האזור שמתחת למטען מגודר ואין שהייה מתחת למטען מונף',
        'תצפית בזמן הנפה',
        `${BUILDING}; ${LIFTING}`,
      ),
      u(
        'C15-07',
        'קיימת מניעת התנגשות עם עגורנים סמוכים, מבנים וקווי חשמל',
        'תיאום בין עגורנים; מגבילי תנועה',
        `${LIFTING}; ${ELECTRICITY}`,
      ),
      u(
        'C15-08',
        'במות הרמה ניידות עם אישורים בתוקף ומפעילים שהוכשרו',
        'תסקיר; הסמכה',
        `${LIFTING}; ${HEIGHT}`,
      ),
      u(
        'C15-09',
        'העבודה בעגורן מופסקת ברוח חזקה לפי הוראות היצרן',
        'מד רוח; הוראה ידועה',
        LIFTING,
      ),
    ]),
    optionalChapter('16', 'חשמל זמני', [
      u(
        'C16-01',
        'לוחות החשמל סגורים, נעולים ומוגנים מפני מים ופגיעה',
        'מצב הלוחות',
        'תקנות החשמל (מיתקן חשמלי ארעי באתר בנייה)',
      ),
      u('C16-02', 'קיימת הארקה ואישור בודק חשמל בתוקף למיתקן', 'אישור בודק', ELECTRICITY),
      u('C16-03', 'מפסקי פחת מותקנים, נבדקים ופועלים', 'לחצן בדיקה; רישום', ELECTRICITY),
      u(
        'C16-04',
        'כבלים מורמים או מוגנים מפני דריסה ומשיכה',
        'כבלים במעברים ובדרכי כלים',
        ELECTRICITY,
      ),
      u(
        'C16-05',
        'כבלי הארכה וכלי עבודה חשמליים ללא בידוד פגום',
        'בדיקה חזותית',
        `${ELECTRICITY}; ${ORDINANCE}`,
      ),
      u('C16-06', 'חיבורי החשמל בוצעו בידי חשמלאי מוסמך', 'רישיון; תיעוד', 'חוק החשמל'),
      u(
        'C16-07',
        'נשמר מרחק בטוח משנאים ומקווי חשמל עיליים, עם סימון והגנה',
        'עבודה וכלים ליד קווים',
        `${ELECTRICITY}; ${BUILDING}`,
      ),
      u('C16-08', 'התאורה הזמנית תקינה ומוגנת', 'גופי תאורה, מתח, הגנה מכנית', ELECTRICITY),
    ]),
    optionalChapter('17', 'ציוד מכני, כלי רכב וכלי עבודה', [
      u(
        'C17-01',
        'מפעילי ציוד נייד (מלגזה, מחפרון, מערבל, מכבש) מוסמכים ומורשים',
        'רישיונות מול המפעילים',
        `${CRANE_OPERATORS}; פקודת התעבורה`,
      ),
      u('C17-02', 'הבדיקות התקופתיות של הציוד בתוקף', 'תסקירים / טסט', `${ORDINANCE}; ${LIFTING}`),
      u(
        'C17-03',
        'נתיבי תנועה מסומנים והולכי רגל מופרדים מכלי רכב',
        'הפרדה פיזית או סימון',
        BUILDING,
      ),
      u(
        'C17-04',
        'קיימת התרעה בנסיעה לאחור, פנס מהבהב ומראות תקינות, או מכוון',
        'צופר ואור; מראות; מכוון בפועל',
        BUILDING,
      ),
      u(
        'C17-05',
        'אביזרי הבטיחות בציוד קיימים ותקינים',
        'חגורה, מגן מפעיל, כיסוי חלקים נעים',
        ORDINANCE,
      ),
      u(
        'C17-06',
        'הציוד חונה ביציבות, לא על מדרון ולא על שפת חפירה',
        'מיקום החניה; בלימה',
        BUILDING,
      ),
      u(
        'C17-07',
        'תדלוק ואחסון דלק נעשים באזור מוגדר ובטוח',
        'אזור מוגדר · מרחק ממקורות הצתה · מיכל על מאצרה, מוארק, בלי שפך',
        `${BUILDING}; ${FIRE}`,
      ),
      u(
        'C17-08',
        'כלי עבודה מכניים (משורים, משחזות, מקדחים) עם מגנים תקינים',
        'מצב המגנים; שימוש',
        ORDINANCE,
      ),
    ]),
    optionalChapter('18', 'אש, עבודות חמות וחומרים מסוכנים', [
      u(
        'C18-01',
        'עבודות חמות (ריתוך, חיתוך, להביור) מבוצעות בהיתר ועם ציוד כיבוי בהישג יד',
        'היתר עבודה חמה; מטף ליד העבודה',
        `${FIRE}; ${ORDINANCE}`,
      ),
      u(
        'C18-02',
        'בלוני גז זקופים, מקובעים, מרוחקים ממקורות חום ועם מונעי החזרת להבה',
        'מצב הבלונים והאביזרים',
        `${ORDINANCE}; תקני גז`,
      ),
      u(
        'C18-03',
        'חומרים דליקים ומסוכנים מאוחסנים בנפרד, מסומנים, ועם גיליונות בטיחות זמינים',
        'מחסן חומרים; סימון; גיליונות',
        `תקנות הבטיחות בעבודה (גיליון בטיחות, סיווג, אריזה, תיווי וסימון); ${FIRE}`,
      ),
      u(
        'C18-04',
        'אבק (למשל סיליקה מחיתוך) מופחת בהרטבה או בשאיבה והעובדים מוגנים',
        'שיטת העבודה; מיגון נשימה',
        'תקנות הבטיחות בעבודה (גיהות תעסוקתית ובריאות העובדים באבק מזיק)',
      ),
      u(
        'C18-05',
        'החשיפה לרעש מופחתת והגנת שמיעה בשימוש',
        'מקורות רעש; מיגון',
        'תקנות הבטיחות בעבודה (גיהות תעסוקתית ובריאות העובדים ברעש)',
      ),
      u(
        'C18-06',
        'בחשד לאסבסט בהריסה או בפירוק העבודה נעצרת והעניין מועבר לגורם מוסמך',
        'נוהל ידוע; עצירה בפועל',
        'חוק למניעת מפגעי אסבסט ואבק מזיק',
      ),
    ]),
    stageChapter('19', 'עבודות גמר וחזיתות', stage(['finishing'], 'גמר וחזיתות'), [
      u(
        'C19-01',
        'פיגומי חזית, במות תלויות ומעליות בנייה עם בדיקה ואישור בתוקף',
        'תסקירים מול הציוד',
        `${BUILDING}; ${LIFTING}`,
      ),
      u(
        'C19-02',
        'מרפסות, חדרי מדרגות ופתחי חלונות מוגנים במעקה עד ההתקנה הסופית',
        'הגנה זמנית בכל קומה',
        BUILDING,
      ),
      u('C19-03', 'פינוי פסולת מגובה נעשה בשוט או במיכל סגור, לא בזריקה', 'אופן הפינוי', BUILDING),
      u(
        'C19-04',
        'אוורור נאות בעבודות צבע, דבקים ואיטום, והחומרים המסוכנים מסומנים',
        'אוורור; מיגון נשימה; סימון',
        'תקנות הבטיחות בעבודה (גיהות תעסוקתית)',
      ),
      u(
        'C19-05',
        'הובלה והתקנה של זכוכית ורכיבים כבדים נעשות בציוד ובאמצעי מיגון מתאימים',
        'כלי הרמה; כפפות; הגנת עיניים',
        `${BUILDING}; ${PPE}`,
      ),
      // בקובץ: תחולה ״עבודות שיפוץ / מבנה פעיל״ — אין עובדה כזו בפרופיל; נשאר לפי הפרק
      // (שלב גמר) עד הכרעה. ראו הערת הקובץ למעלה.
      u(
        'C19-06',
        'קיימת הפרדה בין אזורי בנייה לאזורים מאוכלסים או פעילים',
        'מחיצות, שילוט, דרכי גישה נפרדות',
        BUILDING,
      ),
      u(
        'C19-07',
        'מערכות חשמל קבועות וזמניות אינן מעורבבות ואין חיבור פעיל לא בטוח',
        'לוחות; חיבורים זמניים',
        ELECTRICITY,
      ),
      u('C19-08', 'חדרי מדרגות ומעברים מוארים, נקיים ופנויים', 'תאורה; פסולת גמר', BUILDING),
    ]),
    chapter('20', 'חירום, כיבוי ועזרה ראשונה', [
      u(
        'C20-01',
        'קיימת תוכנית חירום ופינוי והעובדים מכירים אותה',
        'תוכנית; הדרכה; תרגול',
        SAFETY_PLAN,
      ),
      u('C20-02', 'דרכי המילוט פנויות, מסומנות ומוארות', 'סיור בדרכי המילוט', FIRE),
      u('C20-03', 'אמצעי כיבוי זמינים, בתוקף ובמקומות מסומנים', 'מטפים; תאריכי בדיקה; שילוט', FIRE),
      u(
        'C20-04',
        'ערכת עזרה ראשונה מלאה ולפחות אדם אחד מוסמך בעזרה ראשונה',
        'תכולת הערכה; מגיש עזרה ראשונה בנוכחות',
        'תקנות הבטיחות בעבודה (עזרה ראשונה במקומות עבודה)',
      ),
      u('C20-05', 'מספרי חירום וכתובת האתר מוצגים בכניסה', 'שלט קריא', BUILDING),
      u('C20-06', 'רכבי הצלה יכולים להגיע לאתר ולאזורי העבודה', 'דרך גישה פנויה; שער נפתח', FIRE),
    ]),
  ],
};
